import accountantApi from "api/accountant";
import Tooltip from "components/Tooltip";
import {certificationImages} from "constants/certifications";
import {Trash2} from "lucide-react";
import {FC, useEffect, useRef, useState} from "react";
import {useMutation} from "@tanstack/react-query";

type CertificationsRecord = Record<string, string | {url: string; imageUrl?: string}>;

interface Props {
  name: string;
  url: string;
  imageUrl?: string;
  disabled?: boolean;
  editable?: boolean;
  tooltipPosition?: "top" | "bottom";
  onChange?: (name: string, updated: CertificationsRecord) => void;
}

const certColors = ["#57B98F", "#F57A47", "#3D52AD", "#52A882"] as const;

function colorForName(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return certColors[Math.abs(hash) % certColors.length];
}

function shortLabel(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 3).toUpperCase();
}

/* ── Credly image cache ───────────────────────────────────────────────────── */
const memCache = new Map<string, {imageUrl?: string}>();

function getCached(url: string): {imageUrl?: string} | null {
  if (memCache.has(url)) return memCache.get(url)!;
  try {
    const raw = localStorage.getItem(`credly:${url}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      memCache.set(url, parsed);
      return parsed;
    }
  } catch {}
  return null;
}

function setCache(url: string, data: {imageUrl?: string}) {
  memCache.set(url, data);
  try {
    localStorage.setItem(`credly:${url}`, JSON.stringify(data));
  } catch {}
}

const Certification: FC<Props> = ({name, url, imageUrl, disabled, editable, tooltipPosition = "top", onChange}) => {
  const [editing, setEditing] = useState(false);
  const [draftUrl, setDraftUrl] = useState(url);
  const [preview, setPreview] = useState<{name?: string; imageUrl?: string} | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewUrlRef = useRef("");
  const [fetchedImageUrl, setFetchedImageUrl] = useState<string | undefined>(() => {
    if (imageUrl || !url || !url.includes("credly.com/badges/")) return undefined;
    return getCached(url)?.imageUrl;
  });
  const popoverRef = useRef<HTMLDivElement>(null);

  const loadPreview = (value: string) => {
    if (!value || !value.includes("credly.com/badges/")) {
      setPreview(null);
      previewUrlRef.current = "";
      return;
    }
    if (previewUrlRef.current === value) return;
    previewUrlRef.current = value;
    setPreviewing(true);
    setPreview(null);
    accountantApi
      .loadCertificationData(value)
      .then((data) => {
        if (previewUrlRef.current === value) setPreview(data);
      })
      .finally(() => setPreviewing(false));
  };

  const {mutate, isPending} = useMutation({
    mutationFn: (newUrl: string) => {
      const cert = preview?.imageUrl ? {url: newUrl, imageUrl: preview.imageUrl} : newUrl;
      return accountantApi.updateCertification(name, cert || null);
    },
    onSuccess: (updated) => {
      onChange?.(name, updated);
      setEditing(false);
    },
  });

  const {mutate: deleteMutate, isPending: isDeleting} = useMutation({
    mutationFn: () => accountantApi.updateCertification(name, null),
    onSuccess: (updated) => {
      onChange?.(name, updated);
      setEditing(false);
    },
  });

  useEffect(() => {
    if (imageUrl || !url || !url.includes("credly.com/badges/")) return;
    const cached = getCached(url);
    if (cached) {
      if (cached.imageUrl) setFetchedImageUrl(cached.imageUrl);
      return;
    }
    accountantApi.loadCertificationData(url).then((data) => {
      setCache(url, {imageUrl: data.imageUrl});
      if (data.imageUrl) setFetchedImageUrl(data.imageUrl);
    });
  }, [url, imageUrl]);

  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      if (!popoverRef.current?.contains(e.target as Node)) setEditing(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editing]);

  const color = colorForName(name);
  const short = shortLabel(name);
  const resolvedImageUrl = imageUrl || fetchedImageUrl || certificationImages[name];

  const tileInner = resolvedImageUrl ? (
    <img
      src={resolvedImageUrl}
      alt={name}
      className={["w-14 h-14 rounded-xl object-contain select-none", disabled && "animate-pulse"].asClass}
    />
  ) : (
    <div
      className={
        [
          "w-14 h-14 rounded-xl flex items-center justify-center text-sm font-bold select-none text-white",
          disabled && "animate-pulse",
        ].asClass
      }
      style={{backgroundColor: color}}
    >
      {short}
    </div>
  );

  if (!editable) {
    return (
      <Tooltip title={name} subtitle="Earned ✓" position={tooltipPosition} withoutPointer>
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity">
            {tileInner}
          </a>
        ) : (
          tileInner
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip title={name} subtitle="Earned ✓" position={tooltipPosition} withoutPointer>
      <div className="relative" ref={popoverRef}>
        <div className="relative group cursor-pointer" onClick={() => !disabled && setEditing((v) => !v)}>
          {tileInner}
          <div
            className="absolute inset-0 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            style={{backgroundColor: "rgba(0,0,0,0.22)"}}
          >
            <span className="text-white text-xs font-semibold">Edit</span>
          </div>
        </div>

        {editing && (
          <div
            className="absolute top-full left-0 mt-2 z-50 bg-white border border-brand-border rounded-xl p-3 w-64"
            style={{boxShadow: "0 4px 20px rgba(0,0,0,0.1)"}}
          >
            <p className="text-xs font-semibold text-brand-navy mb-2 truncate">{name}</p>
            <input
              type="text"
              value={draftUrl}
              autoFocus
              disabled={isPending}
              onChange={(e) => {
                const val = e.target.value;
                setDraftUrl(val);
                loadPreview(val);
              }}
              placeholder="Credly badge URL (optional)"
              className="w-full text-xs rounded-lg px-2.5 py-1.5 outline-none border mb-2 disabled:opacity-50"
              style={{backgroundColor: "#F7F5F2", borderColor: "#E8E4DF", color: "#282D4D"}}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
              }}
            />
            {previewing && <p className="text-xs text-brand-muted mb-2">Loading…</p>}
            {preview && (
              <div className="flex items-center gap-2 mb-2">
                {preview.imageUrl && (
                  <img
                    src={preview.imageUrl}
                    alt={preview.name}
                    className="w-9 h-9 rounded-lg object-contain flex-shrink-0"
                  />
                )}
                {preview.name && <span className="text-xs font-medium text-brand-navy truncate">{preview.name}</span>}
                {!preview.name && !preview.imageUrl && (
                  <span className="text-xs text-brand-muted">Badge not found</span>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => mutate(draftUrl)}
                disabled={isPending || previewing}
                className="flex-1 text-xs font-semibold py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                style={{backgroundColor: "#3D52AD"}}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setDraftUrl(url);
                  setPreview(null);
                  previewUrlRef.current = "";
                }}
                className="flex-1 text-xs font-semibold py-1.5 rounded-lg border border-brand-border text-brand-muted hover:text-brand-navy transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={isDeleting}
                onClick={() => deleteMutate()}
                className="w-8 flex items-center justify-center rounded-lg border border-red-200 text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default Certification;
